import type { ReminderDraft } from '../../db/models'
import { STANDARD_REMINDER } from '../../reminders/reminderFromDraft'
import { dateKey } from '../../date/calendar'
import { formatImportedFrom } from '../importedFrom'
import { translateRecurrence } from './recurrence'
import { mapDate } from './mapDate'
import type { TodoistReminder } from './types'

/*
  **Todoists Erinnerungen im Import** (Plan task-reminders, Etappe 6). Todoist hat dasselbe Modell:
  eine eigene Zeile je Erinnerung, `relative` vor der Uhrzeit der Aufgabe oder `absolute` mit
  eigenem Zeitpunkt und Rhythmus. Die Vorschau und der Plan fragen dieselbe Stelle, damit die
  Zahlen vorher stimmen.
*/

/** Was mit einer Erinnerung geschieht. */
export type ReminderFate =
  /** Kommt an. */
  | 'arrives'
  /** Erinnert eine andere Person (geteiltes Projekt) — bleibt in Todoist. */
  | 'otherPerson'
  /** Fester Zeitpunkt mit einem Rhythmus, den HappyTask nicht kennt (z. B. „every 2 hours“). */
  | 'rhythm'
  /** Ohne Zeitpunkt oder unbekannte Art — nichts, woran sie klingeln könnte. */
  | 'invalid'

/**
 * Das `due` mit dem LOKALEN Tag: `translateRecurrence` liest Wochen- und Monatstag aus `due.date`.
 * Mit Zone steht dort der Tag in UTC — östlich davon oft der Vortag, und „every week“ klänge dann
 * einen Tag daneben. Der Zeitpunkt selbst ist schon lokal (`mapDate`).
 */
function localDue(due: NonNullable<TodoistReminder['due']>, at: { day: Date; time: string | null }): NonNullable<TodoistReminder['due']> {
  return { ...due, date: `${dateKey(at.day)}T${at.time ?? '00:00'}:00` }
}

export function reminderFate(reminder: TodoistReminder, userId: string | null): ReminderFate {
  // Ohne bekannte eigene ID gilt jede als eigene — lieber eine zu viel als alle weg.
  if (userId && reminder.notify_uid && reminder.notify_uid !== userId) return 'otherPerson'
  if (reminder.type === 'relative') return 'arrives'
  if (reminder.type !== 'absolute' || !reminder.due) return 'invalid'
  if (!mapDate(reminder.due.date)?.time) return 'invalid'
  if (!reminder.due.is_recurring) return 'arrives'
  const rule = translateRecurrence(localDue(reminder.due, mapDate(reminder.due.date)!))
  return rule?.type === 'fixed' ? 'arrives' : 'rhythm'
}

/**
 * **Die Entwürfe einer Aufgabe** — nur, was ankommt:
 *
 * - `relative` → „vorher“ am Wann, derselbe Vorlauf. Todoists automatische Erinnerung steckt nicht
 *   darin — für sie siehe {@link withAutoReminder}.
 * - `absolute` → fester Zeitpunkt als Wanduhr: mit Zeitzone in lokale Zeit umgerechnet, ohne so,
 *   wie es dasteht — wie die Uhrzeiten der Aufgaben (`mapDate`). Der Rhythmus über
 *   `translateRecurrence`.
 * - „Dringend“ wird eine normale Erinnerung; die Vorschau sagt es.
 */
export function buildReminderDrafts(reminders: readonly TodoistReminder[], userId: string | null): ReminderDraft[] {
  return reminders.flatMap((reminder): ReminderDraft[] => {
    if (reminderFate(reminder, userId) !== 'arrives') return []
    const importedFrom = formatImportedFrom('todoist', reminder.id)
    if (reminder.type === 'relative') {
      return [{ kind: 'relative', anchor: 'start', offsetMinutes: Math.max(0, Math.round(reminder.minute_offset ?? 0)), importedFrom }]
    }
    const at = mapDate(reminder.due!.date)!
    return [{
      kind: 'absolute',
      remindDate: dateKey(at.day),
      remindTime: at.time!,
      recurrence: reminder.due!.is_recurring ? translateRecurrence(localDue(reminder.due!, at)) : null,
      importedFrom,
    }]
  })
}

/**
 * **Todoists automatische Erinnerung** (entschieden 30.09.2026): Todoist erinnert an jede Aufgabe
 * mit Uhrzeit, gibt diese Erinnerung aber nicht heraus. Ist sie im Konto an, bekommt die Aufgabe
 * dafür die **Standard-Erinnerung** — mit HappyTasks Vorlauf. Nicht, wenn Todoist schon eine
 * „vorher“-Erinnerung liefert: Sonst klänge es doppelt.
 */
export function withAutoReminder(drafts: ReminderDraft[], hasTime: boolean, autoReminder: number | null): ReminderDraft[] {
  if (autoReminder === null || !hasTime || drafts.some(draft => draft.kind === 'relative')) return drafts
  return [...drafts, STANDARD_REMINDER]
}

/** Erinnerungen je Aufgabe (`item_id`), in Todoists Reihenfolge. */
export function remindersByItem(reminders: readonly TodoistReminder[]): Map<string, TodoistReminder[]> {
  const byItem = new Map<string, TodoistReminder[]>()
  for (const reminder of reminders) byItem.set(reminder.item_id, [...(byItem.get(reminder.item_id) ?? []), reminder])
  return byItem
}
